import React, { useContext } from 'react';
import { BundleSelectionContext } from 'src/components/BundleProduct/BundleSelectionProvider';
import { ArtworkPriceBreakProps } from 'src/components/BundleProduct/BundleOptionsProvider';
import { toUpperCase } from 'src/util/text';
import { Svg } from 'src/components/Svg/Svg';
import { Button } from 'src/components/Button/Button';
import { Price } from 'src/components/Helpers/Price';
import classnames from 'classnames';
import { useVatCausesRender } from 'src/components/AppShell/VatProvider';
import { bundleCustomisationProps } from 'src/components/BundleProduct/utils/formToCustomisation';
import { generateFontImage } from 'src/components/ArtworkApproval/utils/utils';
import { wweBundle_bundleProductDetail_customisation_estimates } from 'src/queries/__generated__/wweBundle';

import classes from './ProductBundleSummary.scss';

const placeholder = require('../../../../static/img/placeholder.jpg');

export const totalColor = (maxQty: number, totalQty: number): string => {
    if (totalQty === maxQty) return 'green';
    if (totalQty > maxQty) return 'red';
    return 'black';
};

export const customizeArtworkData = (artwork: string | undefined) => {
    if (!artwork) {
        return {
            image: '',
            location: '',
            method: 3,
            type: '',
            artworkExist: '',
            noLogoChosen: false,
        };
    }

    const noLogoChosen: boolean = artwork['artwork'] === 'none';

    // Artwork exist
    const existingImage: string = artwork['artwork-existing'] && JSON.parse(artwork['artwork-existing']).image;
    const existingTextImage: string =
        artwork['artwork-existing'] && generateFontImage(JSON.parse(artwork['artwork-existing'])).props.src;
    const artworkExist: string = existingImage || existingTextImage;

    // New artwork
    let image: string = artwork['bundle-customisation']?.['image'];
    const textImage =
        artwork['bundle-customisation']?.['textLineOne'] &&
        generateFontImage(artwork['bundle-customisation']).props.src;
    if (artworkExist || textImage) {
        image = artworkExist || textImage;
    }
    const location: string = artwork['bundle-customisation-location'];
    const method: number = artwork['bundle-customisation']?.['applicationMethod'];
    const type: string =
        artwork['bundle-customisation']?.['applicationType'] || (artwork['artwork-existing'] && 'logo');
    return { image, location, method, type, artworkExist, noLogoChosen };
};

type RenderArtworksProps = {
    position?: string;
    setOpen?: React.Dispatch<React.SetStateAction<number>> | null;
    index?: number;
    artworksData: bundleCustomisationProps;
    artworkCost: ArtworkCostProps;
};

export const RenderArtworks: React.FC<RenderArtworksProps> = (props) => {
    const { position, setOpen, index, artworksData, artworkCost } = props;

    const artworks: any = [];
    for (const [key, value] of Object.entries(artworksData)) {
        const { image, location, method, type } = customizeArtworkData(value);
        const marginTop = key >= '2';
        const typeUpperCase = type && toUpperCase(type);

        artworks.push(
            <Artwork
                key={key}
                simpleArtwork={position === 'summary' ? false : true}
                marginTop={marginTop}
                image={image}
                method={method}
                typeUpperCase={typeUpperCase}
                index={key}
                location={location}
                setOpen={position !== 'summary' ? setOpen : null}
                artworkCost={artworkCost}
            />,
        );
    }

    // If summary display all artworks
    if (position === 'summary') {
        return artworks.map((artwork) => artwork);
    }

    // If customization artwork, dispay the current one
    return artworks.map((artwork, i) => {
        if (i !== index) return null;
        return artwork;
    });
};

export type ArtworkProps = {
    marginTop: boolean;
    image: string;
    method: number;
    typeUpperCase: string;
    index: string;
    location: string;
    simpleArtwork: boolean;
    setOpen?: React.Dispatch<React.SetStateAction<number>> | null;
    artworkCost: ArtworkCostProps;
};

export const sumArtworkPriceAndFee = (artworkCost: ArtworkCostProps) => {
    let artworksPrice = 0;
    let artworksFee = 0;
    if (!artworkCost)
        return {
            artworksPrice,
            artworksFee,
        };
    for (const { price, fee } of Object.values(artworkCost)) {
        artworksPrice += price;
        artworksFee += fee;
    }
    return {
        artworksPrice,
        artworksFee,
    };
};

export const Artwork: React.FC<ArtworkProps> = (props) => {
    const { marginTop, image, method, typeUpperCase, index, location, simpleArtwork, setOpen, artworkCost } = props;
    const { maxQty } = useContext(BundleSelectionContext);
    const { vat } = useVatCausesRender();

    const firstArtwork = index === '1';
    const nextArtwork = index >= '2';
    const methodType = String(method) === '2' ? 'Print' : 'Embroidery';

    let artworkPrice = 0;
    let artworkFee = 0;
    for (const [key, value] of Object.entries(artworkCost)) {
        if (key === index) {
            artworkPrice = value.price;
            artworkFee = value.fee;
        }
    }

    return (
        <div
            className={classnames({
                [classes.bundleArtwork]: true,
                [classes.bundleArtworkMarginTop]: marginTop,
                [classes.bundleArtworkSimple]: simpleArtwork,
            })}
        >
            <div className={classes.bundleArtworkMain}>
                <div className={classes.bundleArtworkLogo}>
                    <img src={image ? image : placeholder} alt="Customisation artwork" />
                </div>

                <div className={classes.bundleArtowrkInfo}>
                    <p className={classes.bundleArtworkInfoTop}>
                        {methodType} {typeUpperCase} <b>x{maxQty}</b>
                    </p>
                    <p className={classes.bundleArtworkInfoBottom}>
                        {location}
                        {simpleArtwork ? null : firstArtwork ? (
                            <span className={classes.bundleArtworkInfoBottomPriceFree}>FREE</span>
                        ) : (
                            <span>
                                <Price price={artworkPrice} priceIncl={artworkPrice} vat={vat} />
                            </span>
                        )}
                    </p>
                </div>

                <div
                    className={classnames({
                        [classes.bundleArtworkPrice]: true,
                        [classes.bundleArtworkPriceSimple]: simpleArtwork,
                    })}
                >
                    {simpleArtwork ? (
                        <>
                            <Button icon="left-of-text" variant="reset" onClick={() => setOpen!(parseInt(index) - 1)}>
                                <Svg name="edit" />
                                <span>Edit</span>
                            </Button>
                            {firstArtwork ? (
                                <p>FREE</p>
                            ) : (
                                <Price price={artworkPrice * maxQty} priceIncl={artworkPrice * maxQty} />
                            )}
                        </>
                    ) : (
                        <>
                            +{' '}
                            <Price
                                price={firstArtwork ? 0 : artworkPrice * maxQty}
                                priceIncl={firstArtwork ? 0 : artworkPrice * maxQty}
                                vat={vat}
                            />
                        </>
                    )}
                </div>
            </div>

            {nextArtwork && !simpleArtwork && (
                <div className={classes.bundleArtworkAdditional}>
                    <p>
                        <b>One-time new logo setup fee</b>
                    </p>
                    <div className={classes.bundleArtworkPrice}>
                        + <Price price={artworkFee} priceIncl={artworkFee} vat={vat} />
                    </div>
                </div>
            )}
        </div>
    );
};

export type ArtworkCostProps = Record<string, { price: number; fee: number }>;

// Return cost and fee for every next artwork
// Return price sum and fee sum of all artworks together

export type Params = {
    artworkPriceBreak?: ArtworkPriceBreakProps;
    artworksData: bundleCustomisationProps;
    maxQty: number;
};

export const organizeArtworkPriceInfo = ({
    artworkPriceBreak,
    artworksData,
    maxQty,
}: Params): {
    artworkCost: ArtworkCostProps;
} => {
    const artworkCost: ArtworkCostProps = {
        [2]: {
            price: 0,
            fee: 0,
        },
    };

    if (Object.keys(artworksData).length <= 1)
        return {
            artworkCost,
        };

    for (const [key, value] of Object.entries(artworksData)) {
        if (key > '1') {
            const { method, type, artworkExist, noLogoChosen } = customizeArtworkData(value);
            const methodType = method === 2 ? 'print' : 'embroidery';

            const artworkPrice = artworkPriceBreak?.customisation_estimates?.filter((el) => {
                if (Number(el?.type) === Number(method) && !noLogoChosen && maxQty >= Number(el?.min_qty)) {
                    return el;
                }
            });

            const artworkFee = artworkPriceBreak?.setup_charges?.find((artwork) => {
                if (!type || !methodType || artworkExist || noLogoChosen) return null;
                if (artwork.type === type && artwork.application === methodType) {
                    return artwork;
                }
            });

            artworkCost[key] = {
                price: artworkPrice && artworkPrice.length > 0 ? artworkPrice[artworkPrice.length - 1].price : 0,
                fee: artworkFee ? artworkFee.price : 0,
            };
        }
    }

    return {
        artworkCost,
    };
};
